import {
  Button,
  CircularProgress, Dialog, DialogContent, DialogTitle,
  Grid,
  Modal,
  ModalContent,
  ModalTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow, Tooltip,
  Typography,
} from "@mui/material";
import { Cancel, Download, UploadFile } from "@mui/icons-material";
import React, { useRef, useState } from "react";
import { fetchS3Attachment } from "../../api/api";
import { TXN_SCOPE } from "../../utilities/constants";
import ToastUtility from "../Utility/toast-utility";

const TransactionAttachmentModal = (props) => {
  const [open, setOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [downloadLoader, setDownloadLoader] = useState("");

  const errorMessages = [];
  const fileInputRef = useRef(null);
  const transactionAttachment = props.transactionAttachments.filter(
    (fileObj) => fileObj.fileStatus && fileObj.fileStatus !== "DELETED"
  );
  const texts = props.texts;

  const handleFileSelect = (event) => {
    const selectedFiles = event.target.files;
    const allowedTypes = [
      "application/pdf",
      "text/plain",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-word",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/csv",
    ];
    const maxSize = 10 * 1024 * 1024;
    const maxFileNameLength = 100;
    const fileNameRegex = /^[a-zA-Z0-9_.() -]{1,100}$/;

    const errorMap = new Map();
    const uniqueFileNames = new Set();
    let filesTobeUploaded = transactionAttachment;
    Array.from(selectedFiles).forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        const error = "File type not supported";
        if (!errorMap.has(error)) {
          errorMap.set(error, []);
        }
        errorMap.get(error).push(file.name);
      } else if (file.size > maxSize) {
        const error = "File size exceeds 10MB limit";
        if (!errorMap.has(error)) {
          errorMap.set(error, []);
        }
        errorMap.get(error).push(file.name);
      } else if (file.name.length > maxFileNameLength) {
        const error = "File name exceeds 100 characters length";
        if (!errorMap.has(error)) {
          errorMap.set(error, []);
        }
        errorMap.get(error).push(file.name);
      } else if (!fileNameRegex.test(file.name)) {
        const error =
          "File name must be alphanumeric with underscores, dots, parentheses and hyphens allowed";
        if (!errorMap.has(error)) {
          errorMap.set(error, []);
        }
        errorMap.get(error).push(file.name);
      } else if (
        filesTobeUploaded.length > 0 &&
        filesTobeUploaded.some(
          (existingFile) => existingFile.fileName === file.name
        )
      ) {
        const error = "File name is already present!";
        if (!errorMap.has(error)) {
          errorMap.set(error, []);
        }
        errorMap.get(error).push(file.name);
      } else if (uniqueFileNames.has(file.name)) {
        const error = "File name is already present!";
        if (!errorMap.has(error)) {
          errorMap.set(error, []);
        }
        errorMap.get(error).push(file.name);
      } else {
        uniqueFileNames.add(file.name);
      }
    });

    errorMap.forEach((files, error) => {
      errorMessages.push(`${error}:\n${files.join(",\n")}`);
    });

    setToastMessage(errorMessages.join("\n\n"));

    const newFiles = Array.from(selectedFiles)
      .filter((file) => {
        for (const files of errorMap.values()) {
          if (files.includes(file.name)) {
            return false;
          }
        }
        return true;
      })
      .map((file) => ({
        fileName: file.name,
        file: file,
        fileStatus: "NEW",
      }));

    props.uploadAttachments(newFiles);
    fileInputRef.current.value = null;
  };

  const closeModel = () => {
    setToastMessage("");
    setOpen(false);
  };
  const handleClearAll = () => {
    if (props.actionType.action !== TXN_SCOPE.VIEW)
      props.clearAllAttachments(true);
  };

  const handleCancelAttachment = (fileName) => {
    props.removeAttachment(fileName);
  };

  const handleDownload = async (objectKey, fileName) => {
    setDownloadLoader(fileName);
    try {
      const response = await fetchS3Attachment(objectKey);
      if (response) {
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = fileName;
        link.click();
      } else {
        setToastMessage("Error: Response is empty");
      }
    } catch (error) {
      setToastMessage("Error downloading file");
    } finally {
      setDownloadLoader("");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sliceStart = page * rowsPerPage;
  const sliceEnd = sliceStart + rowsPerPage;

  return (
    <>
      <Grid
        style={{
          display: "flex",
          justifyContent: "right",
          alignItems: "right",
        }}
        item
        xs={props.actionType.action === TXN_SCOPE.VIEW ? 2 : 1}
      >
        <Button
          size="medium"
          startIcon={<UploadFile />}
          onClick={() => setOpen(true)}
          style={{ backgroundColor: "#FFCC00", margin: "5px" }}
        >
          {texts.ATTACHMENTS_LST_RSN}
        </Button>
        <Dialog
          aria-describedby={"alert-Modal-description"}
          aria-labelledby={"alert-Modal-title"}
          onClose={closeModel}
          open={open}
        >
          <DialogTitle id={"alert-Modal-title"}>{"Attachments"}</DialogTitle>
          <DialogContent
              style={{
              alignItems: "center",
              maxHeight: "400px",
              minHeight: "100px",
              width: "600px",
              overflow: "hidden",
            }}
          >
            {toastMessage && (
              <ToastUtility
                toastDisplay="inline"
                toastVariant="error"
                handleClose={() => setToastMessage("")}
                toastMessage={toastMessage}
              />
            )}

            <Grid alignItems="space-between" container>
              <Grid alignContent="end" item xs={6} a>
                <Button
                  style={{ backgroundColor: "#FFCC00" }}
                  disabled={props.actionType.action === TXN_SCOPE.VIEW}
                  component="label"
                >
                  {texts.UPLOAD_FILE}
                  <input
                    disabled={props.actionType.action === TXN_SCOPE.VIEW}
                    ref={fileInputRef}
                    hidden
                    type="file"
                    onChange={handleFileSelect}
                    multiple
                  />
                </Button>
              </Grid>
              <Grid item xs={6} container justifyContent="flex-end">
                <Button
                  component="label"
                  disabled={props.actionType.action === TXN_SCOPE.VIEW}
                  style={{ alignItems: "end" }}
                  onClick={handleClearAll}
                >
                  {texts.CLEAR_ALL}
                </Button>
              </Grid>
            </Grid>
            <TableContainer
              variant={"default"}
              sx={{ maxHeight: "250px", minHeight: "100px" }}
              style={{ marginTop: "10px" }}
            >
              <Table
                aria-label="sticky table"
                size="small"
                stickyHeader
                striped
              >
                <Typography component={"caption"} variant={"visuallyHidden"}>
                  {"Simple Table"}
                </Typography>
                <TableBody>
                  {transactionAttachment &&
                    transactionAttachment
                      .slice(sliceStart, sliceEnd)
                      .map((file) => {
                        return (
                          <TableRow key={file.fileName} hover>
                            <TableCell sx={{ fontSize: 12 }}>
                              {file.fileName}
                            </TableCell>
                            {props.actionType?.action !== TXN_SCOPE.VIEW && (
                              <TableCell sx={{ width: 10 }} align={"right"}>
                                <Tooltip
                                    placement="top"
                                    title="Cancel"
                                >
                                <Cancel
                                  style={{
                                    color: "black",
                                    cursor: "pointer"
                                  }}
                                  onClick={() =>
                                    handleCancelAttachment(file.fileName)
                                  }
                                />
                                </Tooltip>
                              </TableCell>
                            )}
                            <TableCell
                                sx={{
                                  width: 10,
                                  display: file.fileStatus === 'NEW' && props.actionType.action === "Create" ? 'none' : 'table-cell'
                                }}
                                align={"right"}>
                              {downloadLoader === file.fileName ? (
                                  <CircularProgress
                                      size="small"
                                      style={{color: "#000", marginLeft: "7px"}}
                                  />
                              ) : (
                                  <Tooltip
                                      placement="top"
                                      title="Download"
                                  >
                                    <Download
                                        style={{
                                          cursor: file.fileStatus === 'NEW' ? 'not-allowed' : 'pointer',
                                          opacity: file.fileStatus === 'NEW' ? '0.5' : '1'
                                        }}
                                        onClick={() => {
                                          file.fileStatus && file.fileStatus !== 'NEW' &&
                                            handleDownload(file.fileLocation, file.fileName);

                                        }}
                                    />
                                  </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                </TableBody>
              </Table>
            </TableContainer>
            {transactionAttachment && transactionAttachment.length > 0 && (
              <TablePagination
                component="div"
                count={transactionAttachment ? transactionAttachment.length : 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 100]}
              />
            )}
            <div>
              <Typography variant="caption" color="primary">
                {texts.ATTACHMENTS_NOTE}{" "}
              </Typography>
              <Typography
                style={{
                  margin: "10px -10",
                  textAlign: "left",
                  fontSize: "12px",
                }}
                variant="caption"
                color="textSecondary"
              >
                {texts.ATTACHMENT_UPLOAD_NOTE1}
                <br />
              </Typography>
              <Typography
                style={{ marginLeft: "40px", fontSize: "12px" }}
                variant="caption"
                color="textSecondary"
              >
                {texts.ATTACHMENT_UPLOAD_NOTE2}
              </Typography>
            </div>
          </DialogContent>
        </Dialog>
      </Grid>
      {toastMessage && (
        <ToastUtility
          toastDisplay="inline"
          toastVariant="error"
          handleClose={() => setToastMessage("")}
          toastMessage={toastMessage}
        />
      )}
    </>
  );
};

export default TransactionAttachmentModal;
